import { useState } from 'react';
import { ExternalLink } from 'lucide-react';
import { readTaskDetails } from '@timato/shared';
import { toast } from 'sonner';
import { Modal, ProgressRing } from '@/components/ui';
import { LabelPicker } from '@/components/labels';
import { useProjectMap, useProjects, useTask } from '@/data/hooks';
import { saveRow } from '@/data/repo';
import { deleteSession, longestIdle, splitSession } from '@/data/sessions';
import { setTaskFinished } from '@/data/tasks';
import { TRACKER_NAME } from '@/features/integrations/trackers';
import { cn } from '@/lib/cn';
import { projectColor } from '@/lib/projectColors';
import { formatClock, formatDuration, formatLogged } from '@/lib/time';
import { useCompletionStore } from '@/timer/completionStore';
import type { TimerApi } from '@/timer/useTimer';
import { SessionDialog } from '@/features/sessions/SessionDialog';
import { useFormats } from '@/lib/formats';

const pill = 'h-[50px] rounded-[25px] border-0 font-extrabold text-[14.5px] cursor-pointer inline-flex items-center justify-center gap-[9px] transition-opacity hover:opacity-90';
const ghost = 'flex-1 h-11 rounded-[22px] border border-chip-border bg-transparent text-[13px] font-extrabold text-ink-body cursor-pointer hover:bg-inset';

/** End of session (8f): centred, the logged time in a ring, then the goal, its labels, and what to do next. */
export function EndOfSessionCard({ api }: { api: TimerApi }) {
  const { time } = useFormats();
  const { completion, set } = useCompletionStore();
  const projects = useProjectMap();
  const allProjects = useProjects();
  const task = useTask(completion?.session.taskId ?? null);
  const [adjust, setAdjust] = useState(false);
  // The timer hook already ticks; read its remaining seconds rather than running a second clock.
  const autoStarting = completion?.kind === 'focus' && api.timer.status === 'running' && api.timer.mode !== 'focus';
  const countdown = autoStarting && api.remaining !== null ? Math.max(0, Math.ceil(api.remaining)) : null;

  if (!completion || completion.kind === 'break') return null;
  const { session, kind } = completion;
  const project = session.projectId ? projects.get(session.projectId) : null;
  const logged = task ? formatLogged(task.trackedSec, task.estimateSec) : null;
  const idle = kind === 'track' ? longestIdle(completion.timer.idleSegments) : null;
  const close = () => set(null);
  const source = task ? readTaskDetails(task).source : null;

  const markDone = async () => { if (task) await setTaskFinished(task, true); close(); };
  const discard = async () => { await deleteSession(session); close(); toast('Session discarded'); };
  const split = async () => { if (!idle) return; await splitSession(session, idle.start, idle.end); close(); toast('Session split in two', { description: `${formatDuration(idle.sec)} of idle time dropped.` }); };
  const setLabels = (ids: string[]) => void saveRow('sessions', { ...session, labelIds: ids });

  const nextMode = api.timer.mode === 'long' ? 'Long break' : 'Short break';
  const nextLength = formatDuration(api.timer.durationSec ?? 0, { zero: '' });

  return (
    <>
      <Modal open={!adjust} onOpenChange={(o) => !o && close()} size="sm" title={kind === 'focus' ? 'Focus complete' : 'Tracking stopped'}>
        <div className="flex flex-col items-center gap-[22px] text-center">
          <p className={cn('text-[11px] font-extrabold uppercase tracking-[.09em] m-0 -mt-2', kind === 'focus' ? 'text-accent-text' : 'text-warning-fg')}>
            {kind === 'focus' ? 'Focus complete' : 'Tracking stopped'}
          </p>

          <ProgressRing size={200} progress={1} color={projectColor(project)}>
            <div>
              <div className="font-mono font-medium tabular-nums text-ink" style={{ fontSize: session.durationSec >= 3600 ? 36 : 44, letterSpacing: '-.04em' }}>{formatClock(session.durationSec)}</div>
              <p className="text-[11.5px] font-extrabold uppercase tracking-[.06em] text-ink-2 m-0 mt-1">logged</p>
            </div>
          </ProgressRing>

          <div className="max-w-[330px]">
            <p className="text-[20px] font-extrabold tracking-[-.02em] text-pretty m-0">{session.goalText || <span className="text-ink-2">No goal</span>}</p>
            <p className="text-[12.5px] font-bold text-ink-2 m-0 mt-[6px]">
              {project ? project.name : 'No project'}
              {kind === 'track' && <> · <span className="font-mono text-ink-body">{time(session.startedAt)} → {time(session.endedAt)}</span></>}
              {logged && <> · now <span className="font-mono text-ink-body">{logged.text}</span>{logged.over && <span className="text-warning-fg"> {logged.over}</span>}</>}
            </p>
          </div>

          <LabelPicker value={session.labelIds} onChange={setLabels} project={project} />

          {kind === 'track' && idle && (
            <p className="text-[12.5px] font-semibold text-ink-body max-w-[330px] m-0">Long one. You had <span className="font-mono text-ink">{formatDuration(idle.sec)}</span> idle at {time(idle.start)} - split it into two sessions there?</p>
          )}

          <div className="w-full max-w-[330px] flex flex-col gap-[9px]">
            {kind === 'focus' ? (
              <>
                {api.timer.mode !== 'focus' && (
                  <button type="button" className={cn(pill, 'bg-timer-btn-bg text-timer-btn-fg')} onClick={() => { if (api.timer.status !== 'running') void api.actions.start(); close(); }}>
                    {api.timer.status === 'running' ? `${nextMode} running` : `Start ${nextMode}`}
                    {nextLength && <span className="font-mono text-[12px] opacity-70">{nextLength}</span>}
                  </button>
                )}
                <div className="flex gap-[9px]">
                  <button type="button" className={ghost} onClick={() => void api.actions.startFocusNow()}>Another focus</button>
                  {/* TW-55: a ticket is done in its own tool; ticking it here would be undone by the next sync. */}
                  {task && !task.finishedAt && (source
                    ? <a href={source.url} target="_blank" rel="noopener noreferrer" className={cn(ghost, 'inline-flex items-center justify-center gap-[6px] no-underline')}>Mark done in {TRACKER_NAME[source.kind]}<ExternalLink size={13} aria-hidden="true" /></a>
                    : <button type="button" className={ghost} onClick={() => void markDone()}>Mark task done</button>)}
                </div>
                <div className="flex justify-between text-[11.5px] font-bold text-ink-2 mt-[2px]">
                  <span>{countdown !== null ? <>Break starts itself in <span className="font-mono text-ink-body">{String(countdown).padStart(2, '0')}s</span></> : api.settings.autoStartBreaks ? 'Breaks start themselves' : 'Breaks wait for you'}</span>
                  <span>{api.cycle.session}</span>
                </div>
              </>
            ) : (
              <>
                <button type="button" className={cn(pill, 'bg-timer-btn-bg text-timer-btn-fg')} onClick={close}>Save session</button>
                <div className="flex gap-[9px]">
                  <button type="button" className={ghost} onClick={() => setAdjust(true)}>Adjust time</button>
                  {idle && <button type="button" className={ghost} onClick={() => void split()}>Split at idle</button>}
                </div>
                <div className="flex justify-between text-[11.5px] font-bold text-ink-2 mt-[2px]">
                  <span>No auto-break - Track has no cycle</span>
                  <button type="button" onClick={() => void discard()} className="text-danger-fg font-bold bg-transparent border-0 p-0 cursor-pointer hover:underline">Discard</button>
                </div>
              </>
            )}
          </div>
        </div>
      </Modal>
      <SessionDialog open={adjust} session={session} projects={allProjects.filter((p) => !p.archivedAt)} onOpenChange={(o) => { setAdjust(o); if (!o) close(); }} />
    </>
  );
}
